import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { FadeIn } from '../ui/FadeIn';
import { SectionLabel } from '../ui/SectionLabel';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const menuData = [
  {
    category: 'Set Lunch',
    images: [
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1788438388/SET_LUNCH_fwkku4.png'
    ]
  },
  {
    category: 'Food',
    images: [
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1788438475/SOUP_APPETIZERS_upbjxi.png',
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1788438460/Entree_ayjbkh.png',
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1788438478/LE_DESSERT_iskfwy.png'
    ]
  },
  {
    category: 'Beverages',
    images: [
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1778582838/Page_5_kuzabx.png',
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1778582861/Page_6_g9mwn5.png'
    ]
  },
  {
    category: 'Wines',
    images: [
      'https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1778582991/Page_7_vx6wox.png'
    ]
  }
];

export const MenuViewer = () => {
  const [activeCategory, setActiveCategory] = useState('Set Lunch');
  const [activePage, setActivePage] = useState(0);

  const currentCategory = menuData.find(
    (item) => item.category === activeCategory
  );

  useEffect(() => {
    setActivePage(0);
  }, [activeCategory]);

  const handleNext = () => {
    if (activePage < currentCategory.images.length - 1) {
      setActivePage((page) => page + 1);
    }
  };

  const handlePrev = () => {
    if (activePage > 0) {
      setActivePage((page) => page - 1);
    }
  };

  return (
    <section className="py-20 md:py-32 bg-ivory">
      <div className="container mx-auto px-4 md:px-14">
        <FadeIn className="text-center mb-12">
          <SectionLabel>Le Menu</SectionLabel>
          <h2 className="font-serif text-4xl md:text-6xl text-charcoal mb-6 italic">
            Bistro Gastronomy
          </h2>
          <div className="w-16 h-px bg-gold mx-auto" />
        </FadeIn>

        <div className="flex flex-wrap justify-center gap-2 md:gap-x-10 mb-12 border-b border-gold/10 pb-4 overflow-x-auto no-scrollbar">
          {menuData.map((item) => (
            <button
              key={item.category}
              onClick={() => setActiveCategory(item.category)}
              className={`relative py-3 px-4 text-[10px] md:text-xs tracking-[0.3em] uppercase transition-all duration-500 whitespace-nowrap ${
                activeCategory === item.category
                  ? 'text-wine font-semibold'
                  : 'text-charcoal/40 hover:text-gold'
              }`}
            >
              {item.category}
              {activeCategory === item.category && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-wine"
                />
              )}
            </button>
          ))}
        </div>

        <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between">
          <div className="flex gap-4">
            {currentCategory.images.length > 1 &&
              currentCategory.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActivePage(index)}
                  className={`text-[10px] tracking-[0.2em] uppercase transition-all pb-1 border-b-2 ${
                    activePage === index
                      ? 'border-gold text-charcoal'
                      : 'border-transparent text-charcoal/30 hover:text-gold'
                  }`}
                >
                  Page {index + 1}
                </button>
              ))}
          </div>

          {currentCategory.images.length > 1 && (
            <div className="flex gap-2">
              <button
                onClick={handlePrev}
                disabled={activePage === 0}
                className={`p-2 border border-gold/20 rounded-full transition-all ${
                  activePage === 0
                    ? 'opacity-20 cursor-not-allowed'
                    : 'hover:bg-gold hover:text-white'
                }`}
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <button
                onClick={handleNext}
                disabled={activePage === currentCategory.images.length - 1}
                className={`p-2 border border-gold/20 rounded-full transition-all ${
                  activePage === currentCategory.images.length - 1
                    ? 'opacity-20 cursor-not-allowed'
                    : 'hover:bg-gold hover:text-white'
                }`}
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto relative group">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeCategory}-${activePage}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              className="bg-white shadow-2xl border border-gold/5 overflow-hidden"
            >
              <img
                src={currentCategory.images[activePage]}
                alt={`${activeCategory} Menu Page ${activePage + 1}`}
                className="w-full h-auto object-contain"
                loading="eager"
              />
            </motion.div>
          </AnimatePresence>

          <div className="absolute -inset-4 border border-gold/5 pointer-events-none -z-10" />
        </div>

        <FadeIn delay={0.4} className="mt-16 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold/80 font-medium mb-4">
            ✦ Muslim Owned ✦
          </p>
          <p className="text-[9px] text-charcoal/40 max-w-xs mx-auto italic">
            Prices are subject to prevailing government taxes and service charge.
          </p>
        </FadeIn>
      </div>
    </section>
  );
};